import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { guardarExamenImagenExternaService } from "../../../Service/Service.examen";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import { hcError, hcExito } from "../hcSwal";
import HCModal from "../HCModal";
import { DateBadge, Field, OrderBadge, Section, Segmented } from "../HCPiezas";
import TabDiagnosticos from "./TabDiagnosticos";

/* ============================================================================
   Orden de imagen externa — migrado a HCModal.

   Payload y endpoint idénticos: POST
   /examen_registro_cita_imagen_externa_orden.

   Se mantiene «Sala» y NO se cambia por «Establecimiento sugerido»: la tabla
   `examen_imagen_externa_orden` no tiene esa columna y el cliente pidió
   dejarlo como estaba. `examen_externo` sigue siendo texto libre, que es lo que
   la columna guarda.
   ========================================================================== */

const PRIORIDADES = [
  { value: "rutina", label: "Rutina" },
  { value: "control", label: "Control" },
  { value: "urgente", label: "Urgente", peligro: true },
];

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  sala: "",
  motivo: "",
  resumen_clinico: "",
  examen_externo: "",
  IdExamenTipo: [],
};

export default function ModalImagenExterna({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  IdCategoriaExamen,
  ListaCitaPaciente,
}) {
  const [tab, setTab] = useState("orden");
  const [form, setForm] = useState(FORM_VACIO);
  const [prioridad, setPrioridad] = useState("rutina"); // no persiste: no hay columna
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const examenRef = useRef(null);
  const motivoRef = useRef(null);

  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      IdCategoriaExamen,
      numero_orden: `EXA-EXT-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
    });
    setPrioridad("rutina");
    setIntentado(false);
    setTab("orden");
  }, [show, cita, IdCategoriaExamen]);

  const faltantes = useMemo(() => {
    const f = [];
    if (!form.examen_externo.trim()) f.push("Examen");
    if (!form.motivo.trim()) f.push("Motivo");
    if (!form.fecha) f.push("Fecha");
    return f;
  }, [form]);

  const enfermedadActual = cita?.enfermedad_actual_cita?.[0]?.enfermedad_actual || "";

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      const destino = faltantes[0] === "Examen" ? examenRef.current : motivoRef.current;
      destino?.scrollIntoView({ behavior: "smooth", block: "center" });
      destino?.focus?.({ preventScroll: true });
      return;
    }
    setLoading(true);
    const data = await guardarExamenImagenExternaService(form);
    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      hcError("No se pudo guardar", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }
    hcExito("Orden de imagen externa enviada", form.examen_externo);
    setLoading(false);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    onHide && onHide();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
    [cita?.doctor?.prefijo, cita?.doctor?.nombre].filter(Boolean).join(" ") || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="lg"
      title="Orden de imagen externa"
      subtitle={subtitulo}
      meta={[
        <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de la orden" />,
        <OrderBadge key="o" code={form.numero_orden} />,
      ]}
      tabs={[
        { key: "orden", label: "Orden" },
        { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      validation={{ missing: faltantes }}
      primaryLabel="Agregar orden"
      onPrimary={guardar}
      primaryLoading={loading}
      onClose={cerrar}
      onCancel={cerrar}
      hayCambiosSinGuardar={Boolean(form.examen_externo || form.motivo || form.resumen_clinico)}
    >
      {tab === "orden" ? (
        <>
          <Section title="1 · Examen solicitado" hint="Se realiza en un establecimiento externo">
            <div className="hcgrid hcgrid--3">
              <Field
                label="Examen"
                required
                htmlFor="hc-ext-examen"
                hint="Texto libre: es lo que se imprime en la orden."
                error={intentado && !form.examen_externo.trim() ? "Este campo es obligatorio" : null}
              >
                <input
                  id="hc-ext-examen"
                  ref={examenRef}
                  type="text"
                  placeholder="Nombre del examen"
                  aria-invalid={intentado && !form.examen_externo.trim() ? "true" : undefined}
                  value={form.examen_externo}
                  onChange={(e) => setForm({ ...form, examen_externo: e.target.value })}
                />
              </Field>

              <Field label="Prioridad" hint="Referencia para el servicio; no se guarda todavía.">
                <Segmented ariaLabel="Prioridad" options={PRIORIDADES} value={prioridad} onChange={setPrioridad} />
              </Field>

              <Field label="Sala" htmlFor="hc-ext-sala">
                <input
                  id="hc-ext-sala"
                  type="text"
                  placeholder="Sin asignar"
                  value={form.sala}
                  onChange={(e) => setForm({ ...form, sala: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="2 · Justificación clínica">
            <div className="hcgrid hcgrid--2">
              <Field
                label="Motivo de la solicitud"
                required
                htmlFor="hc-ext-motivo"
                error={intentado && !form.motivo.trim() ? "Este campo es obligatorio" : null}
              >
                <textarea
                  id="hc-ext-motivo"
                  ref={motivoRef}
                  rows={4}
                  placeholder="Requerido para enviar la orden"
                  aria-invalid={intentado && !form.motivo.trim() ? "true" : undefined}
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                />
              </Field>
              <Field
                label="Resumen clínico"
                htmlFor="hc-ext-resumen"
                hint={
                  enfermedadActual ? (
                    <button
                      type="button"
                      className="hcbtn hcbtn--link"
                      style={{ minHeight: 0, padding: 0 }}
                      onClick={() => setForm({ ...form, resumen_clinico: enfermedadActual })}
                    >
                      Copiar de enfermedad actual
                    </button>
                  ) : null
                }
              >
                <textarea
                  id="hc-ext-resumen"
                  rows={4}
                  placeholder="Opcional"
                  value={form.resumen_clinico}
                  onChange={(e) => setForm({ ...form, resumen_clinico: e.target.value })}
                />
              </Field>
            </div>
          </Section>
        </>
      ) : (
        <TabDiagnosticos diagnosticos={diagnosticos} onRefrescar={ListaCitaPaciente} />
      )}
    </HCModal>
  );
}
